'use client';

import { useEffect, useId, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { Field, Input } from '@/app/Components/Field';
import { OperationalStatus } from '@/app/Components/OperationalPage';
import type { GpaCourseIntelligence, GpaCourseSearchResponse } from '@/app/lib/gpaCourseIntelligence';

export function CourseIntelligencePicker({
  onSelect,
}: {
  onSelect: (course: GpaCourseIntelligence) => void;
}) {
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GpaCourseIntelligence[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setState('idle');
      return;
    }

    const controller = new AbortController();
    setResults([]);
    setState('loading');
    setActiveIndex(0);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/gpa/course-search?q=${encodeURIComponent(trimmed)}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Course search failed: ${response.status}`);
        const payload = await response.json() as GpaCourseSearchResponse;
        setResults(payload.results);
        setActiveIndex(0);
        setState(payload.results.length ? 'ready' : 'empty');
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('GPA course search failed:', error);
        setResults([]);
        setState('error');
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [query]);

  const choose = (course: GpaCourseIntelligence) => {
    onSelect(course);
    setQuery('');
    setResults([]);
    setState('idle');
    setOpen(false);
  };

  const activeId = open && state === 'ready' && results[activeIndex] ? `${listboxId}-${activeIndex}` : undefined;

  return (
    <div className="relative" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <Field label="Search Rutgers courses">
        {(props) => (
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--ss-text-muted)]" aria-hidden="true" />
            <Input
              {...props}
              className="pl-10"
              value={query}
              placeholder="Try Data Structures or 01:198:112"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={open && state === 'ready'}
              aria-busy={state === 'loading'}
              aria-controls={open && state === 'ready' ? listboxId : undefined}
              aria-activedescendant={activeId}
              onFocus={() => setOpen(state !== 'idle')}
              onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' && results.length) {
                  event.preventDefault();
                  setOpen(true);
                  setActiveIndex((index) => open ? Math.min(index + 1, results.length - 1) : 0);
                } else if (event.key === 'ArrowUp' && results.length) {
                  event.preventDefault();
                  setActiveIndex((index) => Math.max(index - 1, 0));
                } else if (event.key === 'Enter' && open && results[activeIndex]) {
                  event.preventDefault();
                  choose(results[activeIndex]);
                } else if (event.key === 'Escape') {
                  setOpen(false);
                }
              }}
            />
          </div>
        )}
      </Field>

      {open && state !== 'idle' ? (
        <div className="absolute inset-x-0 z-30 mt-2 overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-overlay-surface)] shadow-e2">
          {state === 'loading' ? <p role="status" className="p-4 text-sm text-[var(--ss-text-muted)]">Searching…</p> : null}
          {state === 'empty' ? <p role="status" className="p-4 text-sm text-[var(--ss-text-muted)]">No matching courses. Try a title or course code.</p> : null}
          {state === 'error' ? <p role="alert" className="p-4 text-sm text-[var(--ss-critical)]">Course search is unavailable. Change your search to retry.</p> : null}
          {state === 'ready' ? (
            <ul id={listboxId} role="listbox" aria-label="Rutgers course results" className="max-h-80 divide-y divide-[var(--ss-border)] overflow-y-auto">
              {results.map((course, index) => (
                <li key={course.baseId} role="presentation">
                  <button
                    id={`${listboxId}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`block min-h-16 w-full px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)] ${index === activeIndex ? 'bg-[var(--ss-brand-bg)]' : 'hover:bg-[var(--ss-surface-subtle)]'}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(course)}
                  >
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <strong className="text-[var(--ss-text)]">{course.title}</strong>
                      <span className="font-mono text-xs text-[var(--ss-text-muted)]">{course.displayId}</span>
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--ss-text-muted)]">
                      <span>{course.credits == null ? 'Credits need review' : `${course.credits} credits`}</span>
                      {course.difficulty ? <OperationalStatus tone="info">AI difficulty {course.difficulty.score}/5</OperationalStatus> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
